import { useEffect, useRef } from "react";
import { recordViews } from "../api";

/**
 * Fire a "viewed" record when a session/beacon card actually scrolls into view.
 *
 * Dedupes per page-session (a card seen once won't re-fire until reload) and
 * batches a short debounce window into one POST. The server also collapses
 * repeat views within a few minutes, so this maps to roughly "one view per
 * visit" — what we want for the "N seen" count + activity feed.
 */

type ViewItem = { type: "session" | "beacon"; id: string };

const seen = new Set<string>();
let queue: ViewItem[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function enqueue(item: ViewItem) {
  const key = `${item.type}:${item.id}`;
  if (seen.has(key)) return;
  seen.add(key);
  queue.push(item);
  if (timer != null) return;
  timer = setTimeout(() => {
    const batch = queue;
    queue = [];
    timer = null;
    void recordViews(batch);
  }, 800);
}

export function useRecordView<T extends HTMLElement>(
  type: "session" | "beacon",
  id: string,
) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          enqueue({ type, id });
          io.disconnect();
        }
      },
      { threshold: 0.6 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [type, id]);
  return ref;
}
